import React from 'react';
import { Clock, Info } from 'lucide-react';
import { GRADES_IDE } from '../../../constants/salary';

interface ISSConfigProps {
  config: {
    echelonHistorique: number;
    indiceMajoreHistorique: number;
  };
  onChange: (config: { echelonHistorique?: number; indiceMajoreHistorique?: number }) => void;
  currentEchelon: number;
  currentGrade: number;
}

export default function ISSConfig({ config, onChange, currentEchelon, currentGrade }: ISSConfigProps) {
  // Récupérer les données du grade actuel
  const gradeData = GRADES_IDE.find(g => g.id === currentGrade);
  
  if (!gradeData) {
    return (
      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="text-sm text-yellow-800">
          Impossible de déterminer les échelons historiques. Veuillez sélectionner un grade valide.
        </p>
      </div>
    );
  }

  // Générer les options d'échelons historiques (échelon actuel et 2 précédents)
  const getHistoricalEchelonOptions = () => {
    const options = [];
    console.log('🔍 ISSConfig - getHistoricalEchelonOptions called');
    console.log('🔍 ISSConfig - gradeData:', gradeData);
    console.log('🔍 ISSConfig - currentEchelon:', currentEchelon);
    
    // Échelon actuel
    const currentEchelonData = gradeData.echelons.find(e => e.echelon === currentEchelon);
    if (currentEchelonData) {
      options.push({
        echelon: currentEchelon,
        indiceMajore: currentEchelonData.indiceMajoré,
        label: `Échelon ${currentEchelon} (actuel)`,
        description: `IM ${currentEchelonData.indiceMajoré} - Situation actuelle`
      });
    }
    
    // Échelon précédent (échelon - 1)
    if (currentEchelon > 1) {
      const previousEchelonData = gradeData.echelons.find(e => e.echelon === currentEchelon - 1);
      if (previousEchelonData) {
        options.push({
          echelon: currentEchelon - 1,
          indiceMajore: previousEchelonData.indiceMajoré,
          label: `Échelon ${currentEchelon - 1}`,
          description: `IM ${previousEchelonData.indiceMajoré} - Il y a quelques années`
        });
      }
    }
    
    // Échelon d'avant (échelon - 2)
    if (currentEchelon > 2) {
      const twoEchelonsAgoData = gradeData.echelons.find(e => e.echelon === currentEchelon - 2);
      if (twoEchelonsAgoData) {
        options.push({
          echelon: currentEchelon - 2,
          indiceMajore: twoEchelonsAgoData.indiceMajoré,
          label: `Échelon ${currentEchelon - 2}`,
          description: `IM ${twoEchelonsAgoData.indiceMajoré} - Il y a plusieurs années`
        });
      }
    }
    
    return options;
  };

  const historicalOptions = getHistoricalEchelonOptions();

  const handleEchelonHistoriqueChange = (echelon: number, indiceMajore: number) => {
    console.log('🔧 ISSConfig - handleEchelonHistoriqueChange called with:', { echelon, indiceMajore });
    onChange({
      echelonHistorique: echelon,
      indiceMajoreHistorique: indiceMajore
    });
  };

  const calculateISSAmount = (indiceMajore: number) => {
    // Utiliser la valeur du point d'indice de 2021 (4,6860€)
    const pointValue2021 = 4.6860;
    const traitementAnnuel = indiceMajore * pointValue2021 * 12;
    const issAnnuel = (traitementAnnuel * 13) / 1900;
    return Math.round(issAnnuel * 100) / 100;
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Clock className="w-4 h-4 inline mr-1" />
          Sélectionnez votre échelon en 2021 (pour le calcul ISS)
        </label>
        
        <div className="space-y-2">
          {historicalOptions.map((option) => (
            <button
              key={option.echelon}
              onClick={(e) => {
                e.stopPropagation();
                handleEchelonHistoriqueChange(option.echelon, option.indiceMajore);
              }}
              className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
                config.echelonHistorique === option.echelon
                  ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-800">{option.label}</div>
                  <div className="text-xs text-gray-600">{option.description}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-blue-600">
                    {calculateISSAmount(option.indiceMajore).toFixed(2)}€/mois
                  </div>
                  <div className="text-xs text-gray-500">
                    (avec valeur 2021)
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        <div className="mt-2 text-xs text-gray-500">
          Sélectionnez l'échelon que vous aviez approximativement en 2021 pour un calcul plus précis de l'ISS
        </div>
      </div>
      
    </div>
  );
}